import { useMemo, useState } from 'react';
import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  CreditCard,
  FileDown,
  FileText,
  Loader2,
  Mail,
  Pencil,
  MapPin,
  Network,
  Paperclip,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  Store,
  UserPlus,
  X,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { fileToDoc } from '@/components/apply/ApplyFields';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  useDecideOnboarding,
  useApplyApplication,
  useFinishOnboarding,
  useOnboardingRequests,
  useUpdateOnboarding,
  type OnboardingEditAddress,
  type OnboardingRoleContact,
  type OnboardingStructuredEdits,
  type DedupeMatch,
  type OnboardingEditableField,
  type OnboardingRequestRow,
  type OnboardingStatus,
} from '@/hooks/admin/useOnboarding';
import {
  useCancelOnboardingInvite,
  useCreateOnboardingInvite,
  useOnboardingInvites,
  useResendOnboardingInvite,
  type BusinessCategory,
} from '@/hooks/admin/useOnboardingInvites';
import AdminLinks from '@/pages/admin/AdminLinks';

const FILTERS: { key: OnboardingStatus | 'all'; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

function StatusBadge({ status }: { status: OnboardingStatus }) {
  const map: Record<OnboardingStatus, { cls: string; label: string }> = {
    pending: { cls: 'bg-amber-50 text-amber-800 border-amber-200', label: 'Pending' },
    approved: { cls: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Approved' },
    completed: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Completed' },
    rejected: { cls: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Declined' },
  };
  const { cls, label } = map[status];
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border', cls)}>
      {label}
    </span>
  );
}

/**
 * New accounts arrive two ways — someone fills out an application, or staff
 * hands a chain/franchise its own link — and both are steps in the same
 * "getting a new account into the system" job. One page, two sections,
 * instead of a separate top-nav tab for each.
 */
type Section = 'applications' | 'links';

function SectionSwitcher({ section, onChange }: { section: Section; onChange: (s: Section) => void }) {
  const options: { key: Section; label: string }[] = [
    { key: 'applications', label: 'Applications' },
    { key: 'links', label: 'Chain & franchise links' },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cn(
            'px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-colors border',
            section === o.key
              ? 'bg-brix-ink text-white border-brix-ink'
              : 'text-brix-muted border-brix-border hover:text-brix-ink hover:bg-brix-primary-tint/40',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function AdminOnboarding() {
  const [searchParams, setSearchParams] = useSearchParams();
  const section: Section = searchParams.get('tab') === 'links' ? 'links' : 'applications';
  const setSection = (s: Section) => setSearchParams(s === 'links' ? { tab: 'links' } : {}, { replace: true });

  const [filter, setFilter] = useState<OnboardingStatus | 'all'>('pending');
  const { data, isLoading, isFetching, error, refetch } = useOnboardingRequests(filter);
  const rows = data?.rows ?? [];
  const errorMessage = error instanceof Error ? error.message : null;

  return (
    <div className="px-6 py-6 space-y-5">
      <SectionSwitcher section={section} onChange={setSection} />

      {section === 'links' ? (
        <AdminLinks />
      ) : (
        <>
          <InvitedPanel />

          {/* Filter tabs */}
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-lg cursor-pointer transition-all duration-200',
                  filter === key
                    ? 'bg-brix-primary-tint text-brix-primary font-semibold'
                    : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
                )}
              >
                {label}
              </button>
            ))}
            {isFetching && (
              <div className="flex items-center gap-1.5 text-xs text-brix-muted ml-1">
                <Loader2 size={12} className="animate-spin" />
                Loading
              </div>
            )}
          </div>

          {errorMessage && (
            <div className="rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
              {errorMessage}{' '}
              <button type="button" className="ml-2 underline cursor-pointer" onClick={() => void refetch()}>
                Retry
              </button>
            </div>
          )}

          <div className="rounded-xl border border-brix-border bg-brix-surface overflow-hidden">
            {isLoading ? (
              <div className="px-4 py-10 text-center text-sm text-brix-muted">
                <Loader2 size={16} className="animate-spin inline-block mr-2" />
                Loading requests…
              </div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-brix-muted">
                No {filter === 'all' ? '' : filter} onboarding requests.
              </div>
            ) : (
              rows.map((row) => <RequestRow key={row.id} row={row} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}

const CATEGORY_LABEL: Record<BusinessCategory, string> = {
  chain: 'Chain',
  franchisee: 'Franchisee',
  independent: 'Independent',
};

function CategoryChip({ category }: { category: BusinessCategory }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-teal-50 text-teal-700 border-teal-200">
      <Store size={11} />
      {CATEGORY_LABEL[category]}
    </span>
  );
}

function InvitedPanel() {
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'invited' | 'applied' | 'cancelled' | 'all'>('invited');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isLoading, refetch } = useOnboardingInvites(statusFilter);
  const resend = useResendOnboardingInvite();
  const cancel = useCancelOnboardingInvite();
  const rows = data?.rows ?? [];

  const handleResend = (id: string) => {
    setNotice(null);
    setBusyId(id);
    resend.mutate(id, {
      onSuccess: () => {
        setBusyId(null);
        setNotice('Invite resent.');
      },
      onError: (e) => {
        setBusyId(null);
        setNotice(e instanceof Error ? e.message : 'Resend failed');
      },
    });
  };

  const handleCancel = (id: string) => {
    if (!window.confirm('Cancel this invite? They will no longer be expected.')) return;
    setNotice(null);
    setBusyId(id);
    cancel.mutate(id, {
      onSuccess: () => {
        setBusyId(null);
        setNotice('Invite cancelled.');
      },
      onError: (e) => {
        setBusyId(null);
        setNotice(e instanceof Error ? e.message : 'Cancel failed');
      },
    });
  };

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Sparkles size={15} className="text-brix-primary" />
          <span className="font-medium text-brix-ink text-sm">Invited — waiting to hear back</span>
        </div>
        <Button
          variant="primary"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setInviteOpen(true);
          }}
        >
          <UserPlus size={14} className="mr-1.5" />
          Invite a new customer
        </Button>
      </button>

      {open && (
        <div className="border-t border-brix-border px-4 py-3 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {(['invited', 'applied', 'cancelled', 'all'] as const).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={cn(
                  'px-2.5 py-1 text-xs rounded-md cursor-pointer transition-all duration-200',
                  statusFilter === key
                    ? 'bg-brix-primary-tint text-brix-primary font-semibold'
                    : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
                )}
              >
                {key === 'all' ? 'All' : key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>

          {notice && (
            <div className="rounded-lg border border-brix-border bg-brix-primary-tint/40 px-3 py-2 text-xs text-brix-ink">
              {notice}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-6 text-sm text-brix-muted">
              <Loader2 size={14} className="animate-spin inline-block mr-2" />
              Loading invites…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-6 text-sm text-brix-muted">No invites yet.</div>
          ) : (
            <div className="divide-y divide-brix-border">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-brix-ink truncate">{r.email}</span>
                      <CategoryChip category={r.business_category} />
                      {r.status === 'applied' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
                          Applied
                        </span>
                      )}
                      {r.status === 'cancelled' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-slate-100 text-slate-600 border-slate-200">
                          Cancelled
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-brix-muted mt-0.5">
                      {r.contact_name ? `${r.contact_name} · ` : ''}
                      Invited {new Date(r.invited_at).toLocaleDateString()}
                      {r.resend_count > 0 ? ` · resent ${r.resend_count}×` : ''}
                    </div>
                  </div>
                  {r.status === 'invited' && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        variant="secondary"
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => handleResend(r.id)}
                      >
                        {busyId === r.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        <span className="ml-1.5">Resend</span>
                      </Button>
                      <Button
                        variant="secondary"
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => handleCancel(r.id)}
                      >
                        <X size={13} />
                        <span className="ml-1.5">Cancel</span>
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {inviteOpen && (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onCreated={() => {
            setInviteOpen(false);
            void refetch();
          }}
        />
      )}
    </div>
  );
}

function InviteDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useCreateOnboardingInvite();

  const handleSubmit = () => {
    setError(null);
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    create.mutate(
      {
        email: email.trim(),
        contact_name: contactName.trim() || undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => onCreated(),
        onError: (e) => setError(e instanceof Error ? e.message : 'Could not send invite'),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl border border-brix-border bg-brix-surface p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-brix-ink">Invite a new customer</h3>
          <button type="button" onClick={onClose} className="text-brix-muted hover:text-brix-ink cursor-pointer">
            <X size={16} />
          </button>
        </div>

        <p className="text-[12px] text-brix-muted leading-snug">
          This sends a one-off application link for an independent business. Is this a chain or a
          franchise instead? Use a{' '}
          <Link to="/admin/onboarding?tab=links" onClick={onClose} className="font-semibold underline">
            shared chain/franchise link
          </Link>{' '}
          — it skips terms, credit and tax collection since the master account already covers them.
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="invite-email">Email address</Label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="invite-contact">Contact name (optional)</Label>
          <Input
            id="invite-contact"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Jane Doe"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="invite-notes">Notes (optional, internal only)</Label>
          <Input
            id="invite-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="How this lead came in, etc."
          />
        </div>

        {error && (
          <div className="rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-xs text-brix-warning">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="button" disabled={create.isPending} onClick={handleSubmit}>
            {create.isPending && <Loader2 size={14} className="animate-spin mr-1.5" />}
            Send invite
          </Button>
        </div>
      </div>
    </div>
  );
}

function RequestRow({ row }: { row: OnboardingRequestRow }) {
  const [open, setOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  // Set when approve found existing QBO matches — show the dedupe chooser
  // instead of completing the approval.
  const [dupMatches, setDupMatches] = useState<DedupeMatch[] | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const decide = useDecideOnboarding();
  const finish = useFinishOnboarding();
  const applyApp = useApplyApplication();
  const [applyResult, setApplyResult] = useState<string | null>(null);

  const submittedAgo = useMemo(() => {
    const d = new Date(row.created_at);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }, [row.created_at]);

  const handleApprove = () => {
    setNotice(null);
    setBusyAction('approve');
    decide.mutate(
      { request_id: row.id, action: 'approve' },
      {
        onSuccess: (data) => {
          setBusyAction(null);
          if (data.duplicate && data.matches && data.matches.length > 0) {
            // Already exists in QBO — let the admin link or override.
            setDupMatches(data.matches);
          } else if (data.status === 'approved') {
            setNotice(
              data.sf_setup_job_id
                ? `Approved — setup ticket created (SF job #${data.sf_setup_job_id}). Ops closes it with a $0 invoice to post the customer into QuickBooks.`
                : `Approved, but the setup ticket could not be created${data.setup_job_error ? ` (${data.setup_job_error})` : ''} — create a "NEW CUSTOMER" job in Service Fusion by hand and close it with a $0 invoice.`,
            );
          }
          // Warnings are ADDED to the outcome, never put in its place: a
          // Service Fusion warning used to overwrite "Approved — setup ticket
          // created", so a successful approval read as a failure (1.170).
          if (data.sf_warnings?.length) {
            setNotice((prev) =>
              [prev, 'Heads up (the approval still went through): ' + data.sf_warnings!.join(' ')]
                .filter(Boolean)
                .join(' '),
            );
          }
        },
        onError: (e) => {
          setBusyAction(null);
          setNotice(e instanceof Error ? e.message : 'Approve failed');
        },
      },
    );
  };

  // Prospect IS an existing QBO customer: approve-as-link, then finish (enable +
  // invite from the mirror). No SF create, no two-stage wait.
  const handleLinkExisting = (match: DedupeMatch) => {
    setNotice(null);
    setBusyAction(`link-${match.qbo_customer_id}`);
    decide.mutate(
      { request_id: row.id, action: 'approve', link_qbo_customer_id: match.qbo_customer_id },
      {
        onSuccess: () => {
          finish.mutate(
            { request_id: row.id, qbo_customer_id: match.qbo_customer_id },
            {
              onSuccess: (data) => {
                setBusyAction(null);
                setDupMatches(null);
                setNotice(
                  data.welcome_sent
                    ? `Linked to ${match.display_name} — login created and welcome email sent.`
                    : `Linked to ${match.display_name} — login created (welcome email did not send; resend from the Users tab).`,
                );
              },
              onError: (e) => {
                setBusyAction(null);
                setNotice(e instanceof Error ? e.message : 'Linking failed after approve — retry Finish from the Approved tab.');
              },
            },
          );
        },
        onError: (e) => {
          setBusyAction(null);
          setNotice(e instanceof Error ? e.message : 'Link failed');
        },
      },
    );
  };

  // Override: this is genuinely a new customer despite the name overlap.
  const handleCreateNewAnyway = () => {
    if (!window.confirm(`Create ${row.business_name} as a NEW Service Fusion customer? Only do this if it is not one of the matches above.`)) return;
    setNotice(null);
    setBusyAction('force');
    decide.mutate(
      { request_id: row.id, action: 'approve', force_create: true },
      {
        onSuccess: (data) => {
          setBusyAction(null);
          setDupMatches(null);
          // Warnings are ADDED to the outcome, never put in its place: a
          // Service Fusion warning used to overwrite "Approved — setup ticket
          // created", so a successful approval read as a failure (1.170).
          if (data.sf_warnings?.length) {
            setNotice((prev) =>
              [prev, 'Heads up (the approval still went through): ' + data.sf_warnings!.join(' ')]
                .filter(Boolean)
                .join(' '),
            );
          }
        },
        onError: (e) => {
          setBusyAction(null);
          setNotice(e instanceof Error ? e.message : 'Approve failed');
        },
      },
    );
  };

  const handleReject = () => {
    decide.mutate(
      { request_id: row.id, action: 'reject', reject_reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          setRejecting(false);
          setReason('');
        },
        onError: (e) => setNotice(e instanceof Error ? e.message : 'Reject failed'),
      },
    );
  };

  const handleFinish = () => {
    setNotice(null);
    finish.mutate(
      { request_id: row.id },
      {
        onSuccess: (data) => {
          if (!data.ready) {
            setNotice(data.message ?? 'Not synced into QuickBooks yet — try again after the next sync.');
          } else {
            // chain_notes are the things the chain finish couldn't do for
            // itself (a clashing store number, an address it couldn't write) —
            // each one is a specific place to go fix it by hand.
            setNotice(
              [
                data.welcome_sent
                  ? 'Onboarding complete — login created and welcome email sent.'
                  : 'Onboarding complete — login created (welcome email did not send; resend from the Users tab).',
                data.opens_on ? `Store opens ${data.opens_on} — set its delivery schedule on the Overview tab.` : null,
                ...(data.chain_notes ?? []),
              ]
                .filter(Boolean)
                .join(' '),
            );
          }
        },
        onError: (e) => setNotice(e instanceof Error ? e.message : 'Finish failed'),
      },
    );
  };

  return (
    <div className="border-b border-brix-border last:border-b-0">
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-brix-primary-tint/20 transition-colors">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="shrink-0 text-brix-muted hover:text-brix-ink cursor-pointer"
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 size={14} className="text-brix-primary shrink-0" />
            <span className="font-medium text-brix-ink truncate">{row.business_name}</span>
          </div>
          <div className="text-[11px] text-brix-muted mt-0.5 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Mail size={11} /> {row.email}</span>
            {row.contact_name && <span>{row.contact_name}</span>}
            <span className="inline-flex items-center gap-1"><Clock size={11} /> {submittedAgo}</span>
          </div>
        </div>
        {/* A chain store came through that chain's public link — it inherits
            the master's terms, payment and pricing, so it carries no credit
            application, tax documents or signature to review. */}
        {row.group_master_customer_id && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-sky-50 text-sky-700 border-sky-200">
            <Network size={11} />
            Chain store{row.store_code ? ` · #${row.store_code}` : ''}
          </span>
        )}
        {row.business_category && <CategoryChip category={row.business_category} />}
        {row.credit_requested && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-violet-50 text-violet-700 border-violet-200">
            <CreditCard size={11} />
            Credit{row.credit_terms ? ` · ${row.credit_terms}` : ''}
          </span>
        )}
        <StatusBadge status={row.status} />
        <div className="flex items-center gap-1.5">
          {row.status === 'pending' && (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={handleApprove}
                disabled={busyAction !== null || dupMatches !== null}
              >
                {busyAction === 'approve' ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Check size={12} />
                )}
                Approve
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setOpen(true);
                  setEditing((v) => !v);
                }}
                title="Correct what the applicant typed before this becomes a Service Fusion and QuickBooks customer"
              >
                <Pencil size={12} /> Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRejecting((v) => !v)}
                className="text-red-700 hover:bg-red-50"
              >
                <X size={12} /> Reject
              </Button>
            </>
          )}
          {row.status === 'completed' && row.created_customer_id && (
            <Button
              variant="secondary"
              size="sm"
              disabled={applyApp.isPending}
              title="Re-copy the contacts, accounting email, delivery details and paperwork from this application onto the customer"
              onClick={() => {
                setApplyResult(null);
                applyApp.mutate(
                  { request_id: row.id },
                  {
                    onSuccess: (data) => {
                      const bits = [
                        data.contacts_written > 0
                          ? `${data.contacts_written} contact${data.contacts_written === 1 ? '' : 's'} added`
                          : 'contacts already on file',
                        'paperwork filed in the customer\u2019s documents',
                      ];
                      setApplyResult([...bits, ...data.notes].join(' \u00b7 '));
                    },
                    onError: (e) =>
                      setApplyResult(
                        e instanceof Error ? e.message : 'Could not copy the application details',
                      ),
                  },
                );
              }}
            >
              {applyApp.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <FileDown size={12} />
              )}
              Copy details onto the customer
            </Button>
          )}
          {row.status === 'approved' && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleFinish}
              disabled={finish.isPending || !row.qbo_ready}
              title={
                row.qbo_ready
                  ? 'Import the customer + create their portal login'
                  : 'Waiting for the customer to sync into QuickBooks'
              }
            >
              {finish.isPending ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
              {row.qbo_ready ? 'Finish onboarding (create login)' : 'Awaiting QuickBooks sync'}
            </Button>
          )}
        </div>
      </div>

      {applyResult && (
        <div className="px-4 pb-3 pl-12 text-xs text-brix-muted">{applyResult}</div>
      )}

      {/* Reject reason inline form */}
      {rejecting && row.status === 'pending' && (
        <div className="px-4 pb-3 pl-12 space-y-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for declining (optional — included in the applicant's email)"
            rows={2}
            className="w-full rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink placeholder:text-brix-muted focus:outline-none focus:ring-2 focus:ring-brix-primary/40"
          />
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleReject} disabled={decide.isPending}>
              {decide.isPending && decide.variables?.action === 'reject' ? (
                <Loader2 size={12} className="animate-spin" />
              ) : null}
              Confirm decline
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Dedupe chooser — prospect already looks like an existing QBO customer */}
      {dupMatches && dupMatches.length > 0 && (
        <div className="px-4 pb-3 pl-12 space-y-2">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-2.5">
            <div className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
              <Building2 size={13} /> Looks like an existing customer
            </div>
            <p className="text-[12px] text-amber-800/90 leading-snug">
              These QuickBooks customers match “{row.business_name}”. Link to the right one to onboard
              instantly (no duplicate Service Fusion customer) — or create a new one if it’s genuinely different.
            </p>
            <div className="space-y-1.5">
              {dupMatches.map((m) => (
                <div
                  key={m.qbo_customer_id}
                  className="flex items-center gap-2 rounded-md border border-amber-200 bg-white px-2.5 py-1.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-brix-ink truncate">{m.display_name}</div>
                    <div className="text-[11px] text-brix-muted">
                      QBO #{m.qbo_customer_id}
                      {m.email ? ` · ${m.email}` : ''}
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleLinkExisting(m)}
                    disabled={busyAction !== null}
                  >
                    {busyAction === `link-${m.qbo_customer_id}` ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Check size={12} />
                    )}
                    Link &amp; onboard
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-0.5">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCreateNewAnyway}
                disabled={busyAction !== null}
              >
                {busyAction === 'force' ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
                Create new anyway
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDupMatches(null)} disabled={busyAction !== null}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Notice */}
      {notice && (
        <div className="px-4 pb-3 pl-12">
          <div className="rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-2 text-sm text-brix-ink flex items-center gap-2">
            <ShieldCheck size={14} className="text-brix-primary shrink-0" />
            {notice}
          </div>
        </div>
      )}

      {/* Expandable detail */}
      {open && (
        <div className="px-4 pb-4 pl-12 space-y-4">
          {editing && row.status === 'pending' && (
            <EditRequestPanel row={row} onDone={() => setEditing(false)} />
          )}
          {/* Chain store — the fields a chain link collects, and a note on
              what it deliberately doesn't ask for. */}
          {row.group_master_customer_id && (
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2.5 space-y-2">
              <div className="text-[10px] font-semibold tracking-tight text-sky-800 uppercase inline-flex items-center gap-1">
                <Network size={11} />
                New store for an existing chain
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Detail label="Store number" value={row.store_code} />
                <Detail label="Store phone" value={row.store_phone} />
                <Detail label="Opens" value={row.opens_on} />
              </div>
              <Detail
                label="Wants delivery on"
                value={
                  row.preferred_delivery_days && row.preferred_delivery_days.length > 0
                    ? row.preferred_delivery_days
                        .map((d) => `${d.charAt(0).toUpperCase()}${d.slice(1)}s`)
                        .join(', ')
                    : null
                }
              />
              {row.equipment_interest && row.equipment_interest.length > 0 && (
                /* A requisition seed, not a quote. Foodservice equipment is
                   requisitioned in the foodservice portal (melt-dashboard),
                   which owns the catalog, quote, inventory and install; ERLS
                   enters only if the deal is a LEASE, via a lease quote raised
                   from that requisition. See
                   docs/HANDOFF-FOODSERVICE-ERLS-SEAM.md. */
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                  <div className="text-[10px] font-semibold tracking-tight text-amber-900 uppercase">
                    Equipment wanted — start the requisition in the foodservice portal
                  </div>
                  <ul className="mt-1.5 space-y-0.5">
                    {row.equipment_interest.map((eq) => (
                      <li key={eq.category} className="text-[13px] text-brix-ink">
                        {eq.qty} × {eq.label}
                        {eq.notes ? (
                          <span className="text-brix-muted"> — {eq.notes}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[11px] text-sky-900/80">
                Terms, payment method, billing address, accounting email and pricing all come from
                the chain master — this application doesn't collect them.{' '}
                <Link
                  to={`/admin/customers/${row.group_master_customer_id}/overview`}
                  className="font-semibold underline"
                >
                  Open the chain account →
                </Link>
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Detail label="Business type" value={row.business_type} />
            <Detail label="Estimated volume" value={row.est_volume} />
            <Detail label="Phone" value={row.phone} icon={<Phone size={11} />} />
            <Detail label="Service Fusion ID" value={row.sf_customer_id} />
            <Detail label="Setup ticket (SF job)" value={row.sf_setup_job_id} />
            <Detail
              label={row.billing_address_json ? 'Billing address (verified)' : 'Billing address'}
              value={row.billing_address}
              icon={<MapPin size={11} />}
            />
            {row.delivery_addresses && row.delivery_addresses.length > 1 ? (
              <div className="sm:col-span-2">
                <div className="text-[10px] font-semibold tracking-tight text-brix-muted uppercase inline-flex items-center gap-1">
                  <MapPin size={11} />
                  Delivery locations ({row.delivery_addresses.length})
                </div>
                <ul className="text-sm text-brix-ink mt-0.5 space-y-0.5">
                  {row.delivery_addresses.map((a, i) => (
                    <li key={i}>
                      {a.label ? `${a.label}: ` : ''}
                      {a.formatted ?? [a.line1, a.unit, a.city, a.state, a.zip].filter(Boolean).join(', ')}
                      {a.verified && <span className="text-emerald-700 text-[11px]"> ✓ verified</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <Detail
                label={row.delivery_address_json ? 'Delivery address (verified)' : 'Delivery address'}
                value={row.delivery_address}
                icon={<MapPin size={11} />}
              />
            )}
            <Detail label="DUNS number" value={row.duns_number} />
            <Detail label="Tax ID (EIN)" value={row.tax_id ?? 'Not given'} />
            <Detail
              label="Resale permit #"
              value={
                row.resale_permit_number ??
                (row.sales_tax_acknowledged_at
                  ? 'Not given — applicant confirmed they understand sales tax will be charged'
                  : 'Not given — sales tax applies')
              }
            />
            <Detail label="Delivery days / windows" value={row.delivery_windows} />
            <Detail label="Special instructions" value={row.special_instructions} />
            <div className="sm:col-span-2">
              <Detail label="Notes" value={row.notes} />
            </div>
          </div>

          {/* Tax paperwork — view what came in, and (until Finish) add what the
              applicant sent separately. Finish copies all of it into the
              customer's Account documents. */}
          <TaxPaperworkPanel row={row} />

          {/* Role contacts */}
          {(row.accounting_contact || row.accounting_email || hasAnyContact(row.contacts)) && (
            <div>
              <div className="text-[10px] font-semibold tracking-tight text-brix-muted uppercase mb-1.5">
                Contacts
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(row.accounting_contact || row.accounting_email) && (
                  <ContactCard
                    role="Accounting"
                    contact={{ name: row.accounting_contact ?? undefined, email: row.accounting_email ?? undefined }}
                  />
                )}
                {(['billing', 'orders', 'management'] as const).map((role) => {
                  const c = row.contacts?.[role];
                  if (!c || !(c.name || c.email || c.phone)) return null;
                  return <ContactCard key={role} role={role} contact={c} />;
                })}
              </div>
            </div>
          )}

          {/* Credit application */}
          {row.credit_requested && (
            <div className="rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-2.5 space-y-2">
              <div className="text-[10px] font-semibold tracking-tight text-brix-muted uppercase inline-flex items-center gap-1">
                <CreditCard size={11} />
                Credit application — {row.credit_terms || 'terms not selected'}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Detail label="Entity type" value={row.credit_app?.entity_type ?? null} />
                <Detail label="Years in business" value={row.credit_app?.years_in_business ?? null} />
                <Detail label="Bank" value={row.credit_app?.bank_name ?? null} />
              </div>
              {(row.credit_app?.references ?? []).length > 0 && (
                <div className="space-y-1.5">
                  {(row.credit_app?.references ?? []).map((ref, i) => (
                    <div
                      key={i}
                      className="rounded-md border border-brix-border bg-brix-surface px-2.5 py-1.5 text-sm text-brix-ink"
                    >
                      <span className="font-medium">{ref.company || `Reference ${i + 1}`}</span>
                      <span className="text-[12px] text-brix-muted">
                        {[ref.contact, ref.phone, ref.email].filter(Boolean).map((v) => ` · ${v}`).join('')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Terms acceptance + signature */}
          {row.terms_accepted_at && (
            <div className="space-y-1.5">
              <div className="text-[11px] text-brix-muted inline-flex items-center gap-1.5">
                <ShieldCheck size={11} className="text-brix-primary" />
                Terms &amp; conditions {row.terms_version ? `(v${row.terms_version}) ` : ''}accepted
                {row.authorized_by ? ` by ${row.authorized_by}` : ''} on{' '}
                {new Date(row.terms_accepted_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
                .
              </div>
              {row.signature_url && (
                <div className="inline-block rounded-lg border border-brix-border bg-white px-3 py-1.5">
                  <img
                    src={row.signature_url}
                    alt={`Signature of ${row.authorized_by ?? row.contact_name ?? 'applicant'}`}
                    className="h-14 w-auto"
                  />
                </div>
              )}
            </div>
          )}

          {row.status === 'rejected' && <Detail label="Decline reason" value={row.reject_reason} />}
          {row.status === 'approved' && (
            <div className="text-[11px] text-brix-muted inline-flex items-center gap-1.5">
              <Sparkles size={11} className="text-brix-primary" />
              {row.qbo_ready
                ? 'Matched in QuickBooks — ready to finish onboarding.'
                : row.sf_setup_job_id
                  ? `Service Fusion customer created. Close setup ticket #${row.sf_setup_job_id} with a $0 invoice — posting it syncs the customer into QuickBooks and unlocks the portal login.`
                  : 'Service Fusion customer created. Waiting for the SF→QuickBooks sync to mirror it before the portal login can be created.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function hasAnyContact(
  contacts: Record<string, { name?: string; email?: string; phone?: string }> | null,
): boolean {
  if (!contacts) return false;
  return Object.values(contacts).some((c) => c && (c.name || c.email || c.phone));
}

function ContactCard({
  role,
  contact,
}: {
  role: string;
  contact: { name?: string; email?: string; phone?: string };
}) {
  return (
    <div className="rounded-md border border-brix-border bg-brix-surface px-2.5 py-1.5">
      <div className="text-[10px] font-semibold tracking-tight text-brix-muted uppercase">{role}</div>
      <div className="text-sm text-brix-ink mt-0.5">
        {contact.name || <span className="text-brix-muted italic">No name</span>}
      </div>
      <div className="text-[11px] text-brix-muted flex items-center gap-2 flex-wrap">
        {contact.email && (
          <span className="inline-flex items-center gap-1">
            <Mail size={10} /> {contact.email}
          </span>
        )}
        {contact.phone && (
          <span className="inline-flex items-center gap-1">
            <Phone size={10} /> {contact.phone}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The tax ID, the resale permit number and the two documents — shown whatever
 * the status, and editable while the application is pending OR approved (the
 * server refuses once it is completed; the customer's vault is the place then).
 * Everything here is optional: an account can be approved with none of it, and
 * without a resale permit the customer is simply taxable.
 */
function TaxPaperworkPanel({ row }: { row: OnboardingRequestRow }) {
  const update = useUpdateOnboarding();
  const editable = row.status === 'pending' || row.status === 'approved';
  const [taxId, setTaxId] = useState(row.tax_id ?? '');
  const [permit, setPermit] = useState(row.resale_permit_number ?? '');
  const [busy, setBusy] = useState<null | 'numbers' | 'taxid' | 'resale'>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const numbersChanged =
    taxId.trim() !== (row.tax_id ?? '') || permit.trim() !== (row.resale_permit_number ?? '');

  const saveNumbers = () => {
    setMessage(null);
    setBusy('numbers');
    update.mutate(
      {
        request_id: row.id,
        fields: { tax_id: taxId.trim() || null, resale_permit_number: permit.trim() || null },
      },
      {
        onSuccess: () => {
          setBusy(null);
          setMessage({ tone: 'ok', text: 'Saved — it goes onto the customer when you finish onboarding.' });
        },
        onError: (e) => {
          setBusy(null);
          setMessage({ tone: 'err', text: e instanceof Error ? e.message : 'Could not save' });
        },
      },
    );
  };

  const attach = async (kind: 'taxid' | 'resale', file: File | undefined) => {
    if (!file) return;
    setMessage(null);
    setBusy(kind);
    try {
      const doc = await fileToDoc(file);
      await update.mutateAsync({ request_id: row.id, doc: { kind, ...doc } });
      setMessage({
        tone: 'ok',
        text: `${kind === 'taxid' ? 'Tax ID document' : 'Resale certificate'} attached — it goes into the customer's Account documents when you finish onboarding.`,
      });
    } catch (e) {
      setMessage({ tone: 'err', text: e instanceof Error ? e.message : 'Could not attach that file' });
    } finally {
      setBusy(null);
    }
  };

  const docSlot = (kind: 'taxid' | 'resale', label: string, url: string | null) => (
    <div className="flex items-center gap-2 flex-wrap">
      {url ? (
        <DocLink label={label} url={url} />
      ) : (
        <span className="text-sm text-brix-muted">{label}: not attached</span>
      )}
      {editable && (
        <label
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border border-dashed border-brix-border px-2.5 py-1.5 text-[12px] text-brix-muted hover:text-brix-ink hover:border-brix-primary/50 cursor-pointer',
            busy === kind && 'opacity-60 pointer-events-none',
          )}
        >
          {busy === kind ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
          {url ? 'Replace' : 'Attach'}
          <input
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              void attach(kind, f);
            }}
          />
        </label>
      )}
    </div>
  );

  if (row.group_master_customer_id && !row.tax_id && !row.resale_permit_number && !row.tax_id_doc_url && !row.resale_cert_doc_url) {
    // A chain store's tax status follows its master; nothing to show or ask.
    return null;
  }

  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface px-3 py-3 space-y-2.5">
      <div className="text-[10px] font-semibold tracking-tight text-brix-ink uppercase">
        Tax ID & resale certificate <span className="text-brix-muted normal-case font-normal">— optional</span>
      </div>
      {editable && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-[11px]">Tax ID (EIN)</Label>
            <Input value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="12-3456789" className="mt-1" />
          </div>
          <div>
            <Label className="text-[11px]">Resale permit #</Label>
            <Input value={permit} onChange={(e) => setPermit(e.target.value)} placeholder="CA seller's permit" className="mt-1" />
          </div>
          <Button size="sm" variant="secondary" disabled={!numbersChanged || busy !== null} onClick={saveNumbers}>
            {busy === 'numbers' ? <Loader2 size={13} className="animate-spin" /> : 'Save'}
          </Button>
        </div>
      )}
      {docSlot('taxid', 'Tax ID document', row.tax_id_doc_url)}
      {docSlot('resale', 'Resale certificate', row.resale_cert_doc_url)}
      {message && (
        <p className={cn('text-[12px]', message.tone === 'ok' ? 'text-emerald-700' : 'text-red-700')}>
          {message.text}
        </p>
      )}
      {editable && row.status === 'approved' && (
        <p className="text-[11px] text-brix-muted">
          Service Fusion already has this customer — anything added now lands in the portal and the
          customer&apos;s Account documents when you finish onboarding. Type the permit number into the
          Service Fusion customer&apos;s &ldquo;CA Resale Permit#&rdquo; field by hand (Service Fusion has no
          customer-update API).
        </p>
      )}
    </div>
  );
}

function DocLink({ label, url }: { label: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 rounded-md border border-brix-border bg-brix-surface px-2.5 py-1.5 text-sm text-brix-primary hover:bg-brix-primary-tint transition-colors"
    >
      <FileText size={13} />
      {label}
    </a>
  );
}

/**
 * Correct a PENDING application before it becomes a real customer.
 *
 * The business name is the field that matters: admin-decide-onboarding sends
 * it verbatim as the Service Fusion `customer_name`, and SF's sync names the
 * QuickBooks customer from that — so whatever a store manager typed into the
 * public form is what accounting carries forever. On a chain store the panel
 * shows the resulting QuickBooks name (`Parent:Store`) as you type, because
 * that string is the actual thing being decided.
 *
 * Corrections annotate rather than erase — `original_values` keeps what the
 * applicant wrote, which matters most on a full /apply application, where the
 * form was signed.
 */
function EditRequestPanel({
  row,
  onDone,
}: {
  row: OnboardingRequestRow;
  onDone: () => void;
}) {
  const update = useUpdateOnboarding();
  const isChainStore = !!row.group_master_customer_id;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const [form, setForm] = useState<Record<OnboardingEditableField, string>>({
    business_name: row.business_name ?? '',
    contact_name: row.contact_name ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    store_code: row.store_code ?? '',
    store_phone: row.store_phone ?? '',
    accounting_contact: row.accounting_contact ?? '',
    accounting_email: row.accounting_email ?? '',
    duns_number: row.duns_number ?? '',
    business_type: row.business_type ?? '',
    est_volume: row.est_volume ?? '',
    credit_terms: row.credit_terms ?? '',
    delivery_windows: row.delivery_windows ?? '',
    special_instructions: row.special_instructions ?? '',
    notes: row.notes ?? '',
  });
  const [contacts, setContacts] = useState<Record<RoleKey, OnboardingRoleContact>>(() => ({
    billing: { ...(row.contacts?.billing ?? {}) },
    orders: { ...(row.contacts?.orders ?? {}) },
    management: { ...(row.contacts?.management ?? {}) },
  }));
  const toAddr = (a: Record<string, unknown> | null | undefined): AddrDraft => ({
    label: str(a?.label),
    line1: str(a?.line1),
    unit: str(a?.unit),
    city: str(a?.city),
    state: str(a?.state),
    zip: str(a?.zip),
  });
  const [billing, setBilling] = useState<AddrDraft>(() =>
    row.billing_address_json
      ? toAddr(row.billing_address_json)
      : { ...EMPTY_ADDR, line1: row.billing_address ?? '' },
  );
  const [deliveries, setDeliveries] = useState<AddrDraft[]>(() => {
    const list = Array.isArray(row.delivery_addresses) && row.delivery_addresses.length > 0
      ? row.delivery_addresses
      : row.delivery_address_json
        ? [row.delivery_address_json]
        : [];
    if (list.length > 0) return list.map((a) => toAddr(a as Record<string, unknown>));
    return row.delivery_address ? [{ ...EMPTY_ADDR, line1: row.delivery_address }] : [];
  });
  const [error, setError] = useState<string | null>(null);

  const set = (k: OnboardingEditableField) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const parentName = row.chain_master_name?.trim() ?? '';
  const storeName = form.business_name.trim();

  const blankAddr = (a: AddrDraft) => !a.line1.trim() && !a.city.trim() && !a.state.trim() && !a.zip.trim() && !a.unit.trim();
  const sendAddr = (a: AddrDraft): OnboardingEditAddress => ({
    label: a.label.trim() || null,
    line1: a.line1.trim(),
    unit: a.unit.trim() || null,
    city: a.city.trim(),
    state: a.state.trim().toUpperCase(),
    zip: a.zip.trim() || null,
  });

  const save = () => {
    setError(null);
    const fields: Partial<Record<OnboardingEditableField, string | null>> & OnboardingStructuredEdits = {};
    (Object.keys(form) as OnboardingEditableField[]).forEach((k) => {
      fields[k] = form[k].trim() ? form[k] : null;
    });
    const cleanContacts: Record<string, OnboardingRoleContact> = {};
    ROLE_KEYS.forEach((r) => {
      const c = contacts[r];
      if (c.name?.trim() || c.email?.trim() || c.phone?.trim()) cleanContacts[r] = c;
    });
    fields.contacts = Object.keys(cleanContacts).length ? cleanContacts : null;
    fields.billing_address_json = blankAddr(billing) ? null : sendAddr(billing);
    const dl = deliveries.filter((d) => !blankAddr(d)).map(sendAddr);
    fields.delivery_addresses = dl.length ? dl : null;
    update.mutate(
      { request_id: row.id, fields },
      {
        onSuccess: onDone,
        onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
      },
    );
  };

  const applicantNote = (k: string, current: unknown) => {
    const applicant = row.original_values?.[k];
    if (applicant === undefined || applicant === null) return null;
    if (JSON.stringify(applicant) === JSON.stringify(current)) return null;
    const text =
      typeof applicant === 'string'
        ? applicant
        : typeof applicant === 'object' && applicant && 'formatted' in applicant
          ? String((applicant as { formatted: unknown }).formatted)
          : JSON.stringify(applicant);
    return <p className="text-[10px] text-brix-muted mt-1">Applicant entered: {text}</p>;
  };

  const field = (k: OnboardingEditableField, label: string, placeholder?: string) => (
    <div>
      <Label className="text-[11px]">{label}</Label>
      <Input value={form[k]} onChange={set(k)} placeholder={placeholder} className="mt-1" />
      {applicantNote(k, row[k])}
    </div>
  );
  const area = (k: OnboardingEditableField, label: string, placeholder?: string) => (
    <div className="sm:col-span-2">
      <Label className="text-[11px]">{label}</Label>
      <textarea
        value={form[k]}
        onChange={set(k)}
        placeholder={placeholder}
        rows={2}
        className="mt-1 w-full rounded-md border border-brix-border bg-white px-3 py-2 text-sm text-brix-ink focus:outline-none focus:ring-2 focus:ring-brix-primary/30"
      />
      {applicantNote(k, row[k])}
    </div>
  );
  const section = (title: string) => (
    <div className="sm:col-span-2 pt-2 text-[10px] font-semibold tracking-tight text-brix-muted uppercase border-t border-brix-border">
      {title}
    </div>
  );

  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface px-3 py-3 space-y-3">
      <div className="text-[10px] font-semibold tracking-tight text-brix-ink uppercase inline-flex items-center gap-1">
        <Pencil size={11} />
        Edit this application before approving
      </div>
      <p className="text-[11px] text-brix-muted leading-snug">
        Everything below goes to Service Fusion exactly as typed when you approve. The business name
        becomes the Service Fusion and QuickBooks customer name. What the applicant originally entered
        is kept and shown under any field you change. The signature, terms and attached documents are
        not changed here.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {section('Business')}
        {field('business_name', isChainStore ? 'Store name' : 'Business name')}
        {field('business_type', 'Business type')}
        {field('duns_number', 'DUNS number')}
        {field('est_volume', 'Estimated volume')}
        {isChainStore && field('store_code', 'Store number', 'e.g. 0055')}
        {isChainStore && field('store_phone', 'Store phone')}

        {section('Main contact')}
        {field('contact_name', 'Contact name')}
        {field('email', 'Email')}
        {field('phone', 'Phone')}

        {section('Accounting contact')}
        {field('accounting_contact', 'Accounting contact')}
        {field('accounting_email', 'Accounting email')}

        {section('Other contacts')}
        {ROLE_KEYS.map((r) => (
          <div key={r} className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {(['name', 'email', 'phone'] as const).map((f) => (
              <div key={f}>
                <Label className="text-[11px] capitalize">
                  {r} {f}
                </Label>
                <Input
                  value={contacts[r][f] ?? ''}
                  onChange={(e) => setContacts((c) => ({ ...c, [r]: { ...c[r], [f]: e.target.value } }))}
                  className="mt-1"
                />
              </div>
            ))}
          </div>
        ))}

        {section('Billing address')}
        <AddressEditor value={billing} onChange={setBilling} />
        <div className="sm:col-span-2 -mt-1">{applicantNote('billing_address_json', row.billing_address_json)}</div>

        {section('Delivery locations')}
        {deliveries.map((d, i) => (
          <div key={i} className="sm:col-span-2 rounded-md border border-brix-border px-2.5 py-2 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-brix-ink">Location {i + 1}</span>
              <button
                type="button"
                className="text-[11px] text-red-700 hover:underline"
                onClick={() => setDeliveries((list) => list.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </div>
            <AddressEditor
              value={d}
              withLabel
              onChange={(v) => setDeliveries((list) => list.map((x, j) => (j === i ? v : x)))}
            />
          </div>
        ))}
        <div className="sm:col-span-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setDeliveries((list) => [...list, { ...EMPTY_ADDR }])}
          >
            + Add delivery location
          </Button>
        </div>

        {section('Delivery & notes')}
        {area('delivery_windows', 'Delivery windows')}
        {area('special_instructions', 'Special instructions', 'Gate code, key location…')}
        {field('credit_terms', 'Credit terms', 'e.g. Net 30')}
        {area('notes', 'Notes')}
      </div>

      {isChainStore && (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-2.5 py-2">
          <div className="text-[10px] font-semibold tracking-tight text-sky-800 uppercase">
            Will be created as
          </div>
          <p className="text-[13px] text-brix-ink mt-0.5 font-mono">
            {parentName ? `${parentName}:${storeName || '…'}` : storeName || '…'}
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] text-red-800">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={update.isPending}>
          {update.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          Save changes
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone} disabled={update.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

type RoleKey = 'billing' | 'orders' | 'management';
const ROLE_KEYS: RoleKey[] = ['billing', 'orders', 'management'];

interface AddrDraft {
  label: string;
  line1: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
}
const EMPTY_ADDR: AddrDraft = { label: '', line1: '', unit: '', city: '', state: '', zip: '' };

function AddressEditor({
  value,
  onChange,
  withLabel,
}: {
  value: AddrDraft;
  onChange: (v: AddrDraft) => void;
  withLabel?: boolean;
}) {
  const f = (k: keyof AddrDraft, label: string, className = '', placeholder?: string) => (
    <div className={className}>
      <Label className="text-[11px]">{label}</Label>
      <Input
        value={value[k]}
        placeholder={placeholder}
        onChange={(e) => onChange({ ...value, [k]: e.target.value })}
        className="mt-1"
      />
    </div>
  );
  return (
    <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-6 gap-2">
      {withLabel && f('label', 'Name', 'col-span-2 sm:col-span-6', 'e.g. Main kitchen')}
      {f('line1', 'Street', 'col-span-2 sm:col-span-4')}
      {f('unit', 'Unit / suite', 'col-span-2 sm:col-span-2')}
      {f('city', 'City', 'col-span-2 sm:col-span-3')}
      {f('state', 'State', 'sm:col-span-1', 'CA')}
      {f('zip', 'ZIP', 'sm:col-span-2')}
    </div>
  );
}

function Detail({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | null;
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-tight text-brix-muted uppercase inline-flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="text-sm text-brix-ink mt-0.5 whitespace-pre-wrap">
        {value && value.trim() ? value : <span className="text-brix-muted italic">—</span>}
      </div>
    </div>
  );
}
